import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Flex, // Import Flex for responsive layouts
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import { tableCustomStyles } from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faVial,
  faSyringe as faSyringeIcon,
  faMoneyBillWave,
  faDollarSign,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faCapsules,
  faPrint,
  faFilePdf,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker, { convertToThDate, getDateFromISOStringIgnoringTimezone } from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
// Load Sarabun font for PDF (function remains unchanged)
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(`Failed to fetch Sarabun font: ${fontResponse.statusText}`);
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineReservationForm = () => {
  const navigate = useNavigate();
  const { reservationId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    ReservationID: reservationId || "RV-XXXX-XX-XX",
    ReservationDate: new Date(),
    FarmerID: profile?.FarmerID || "",
    FarmerName:
      [profile?.FirstName, profile?.LastName].filter(Boolean).join(' ') || "",
    RefTaxID: profile?.FarmerID || "",
    FarmerTel: profile?.Phone || "",
    FarmerEmail: profile?.Email || "",
    UsageObjective: "",
    DeliveryPeriod: "",
    PickupPoint: "",
    ReservedBy: profile?.Email || "",
    ReservationStatus: "ใบจองใหม่",
    ConfirmationEmailSent: false,
    ConsentGiven: true,
    Remark: "",
    Status: "Active",
    VaccineDetails: [],
    documentDate: new Date(),
  });

  const [cycles, setCycles] = useState([]);
    const [periodEnd, setPeriodEnd] = useState(null); // Store PeriodEnd date
  const [pickupPoints, setPickupPoints] = useState([]);
  const [newVaccine, setNewVaccine] = useState({ id: "", unitreq: "" });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!reservationId);
  const [errors, setErrors] = useState({});
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = useRef();

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchCycles = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineOrderCycle",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setCycles([response]);
        if (!reservationId) {
          setFormData((prev) => ({
            ...prev,
            DeliveryPeriod: response.CycleName || "",
          }));
          // Fetch PeriodEnd by querying VaccineReservationPeriod
          const periodResponse = await apiService({
            method: "POST",
            url: "/getVaccineReservationPeriod", // Assume new endpoint to fetch period details
            data: { DeliveryPeriod: response.CycleName },
            sessionId: sessionId || "",
          });
          if (periodResponse.result === "success" && periodResponse.PeriodEnd) {
            setPeriodEnd(new Date(periodResponse.PeriodEnd));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching cycles:", error);
    }
  };

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchWarehouseById = async (warehouseId) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesByID",
        data: { WarehouseID: warehouseId },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        return response.WarehouseName || warehouseId; // Return WarehouseName or fallback to ID
      }
      return warehouseId; // Fallback to ID if API fails
    } catch (error) {
      console.error(`Error fetching warehouse ${warehouseId}:`, error);
      return warehouseId; // Fallback to ID on error
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesReservationList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchReservation = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsByID",
        data: { ReservationID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const reservation = response.VaccineReservation;
        const details = response.VaccineReservationDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          if (!vaccine) {
            console.warn(`VaccineID ${detail.VaccineID} not found in vaccineOptions`);
          }
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            RequestDoses: detail.RequestDoses,
            ApprovedQuantity: detail.ApprovedQuantity,
            ConfirmedQuantity: detail.ConfirmedQuantity,
            UnitPrice: detail.UnitPrice,
            DiscountPercent: detail.DiscountPercent,
            DiscountTotal: detail.DiscountTotal,
            TotalPrice: detail.TotalPrice,
            Remark: detail.Remark,
            DoseCount: doseCount,
          };
        });
        setFormData({
          ...formData,
          ...reservation,
          ReservationDate: reservation.ReservationDate ? getDateFromISOStringIgnoringTimezone(reservation.ReservationDate) : new Date(),
          VaccineDetails: updatedDetails,
        });
        // Fetch PeriodEnd for the reservation's DeliveryPeriod
        const periodResponse = await apiService({
          method: "POST",
          url: "/getVaccineReservationPeriod",
          data: { DeliveryPeriod: reservation.DeliveryPeriod },
          sessionId: sessionId || "",
        });
        if (periodResponse.result === "success" && periodResponse.PeriodEnd) {
          setPeriodEnd(new Date(periodResponse.PeriodEnd));
        }
      }
    } catch (error) {
      console.error("Error fetching reservation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        await fetchCycles();
        await fetchPickupPoints();
        const vaccines = await fetchVaccineOptions();
        if (reservationId) {
          await fetchReservation(reservationId, vaccines);
        }
      }
    };
    loadData();
  }, [sessionId, reservationId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, documentDate: date });
  };
  
  const handleDeliveryDateChange = (date) => {
    setFormData({ ...formData, deliveryDate: date });
  };

  const handleVaccineInputChange = (e) => {
    const { name, value } = e.target;
    setNewVaccine({ ...newVaccine, [name]: value });
  };

  const handleQuantityChange = (vaccineId, value) => {
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId) {
        const newQuantity = parseInt(value) || 0;
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 1;
        const newTotalPrice = newQuantity * detail.UnitPrice;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return {
          ...detail,
          RequestQuantity: newQuantity,
          TotalPrice: newTotalPrice,
          DoseCount: newDoseCount,
        };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "DeliveryPeriod":
      case "PickupPoint":
      case "FarmerName":
      case "FarmerTel":
      case "FarmerEmail":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.DeliveryPeriod &&
      formData.PickupPoint &&
      formData.FarmerName &&
      formData.FarmerTel &&
      formData.FarmerEmail &&
      Object.keys(errors).length === 0
    );
  };

  const handleAddVaccine = () => {
    if (newVaccine.id && newVaccine.unitreq > 0) {
      const selectedVaccine = vaccineOptions.find(
        (v) => v.VaccineID === newVaccine.id
      );
      if (selectedVaccine) {
        const existingVaccine = formData.VaccineDetails.find(
          (v) => v.VaccineID === newVaccine.id
        );
        const uop = selectedVaccine.PriceDistribution || "350";
        const packageSize = selectedVaccine.PackageSize || 1;
        const newQuantity = parseInt(newVaccine.unitreq);
        const newDoseCount = Math.ceil(newQuantity * packageSize);

        if (existingVaccine) {
          setIsAlertOpen(true);
        } else {
          const totalCost = (newQuantity * parseInt(uop)).toString();
          const vaccineData = {
            VaccineID: newVaccine.id,
            name: selectedVaccine.VaccineNameTH,
            LotID: `LT${Date.now().toString().slice(-4)}`,
            RequestQuantity: newQuantity,
            RequestDoses: 0,
            ApprovedQuantity: 0,
            ConfirmedQuantity: 0,
            UnitPrice: parseFloat(uop),
            DiscountPercent: 0,
            DiscountTotal: 0,
            TotalPrice: parseFloat(totalCost),
            Remark: "",
            DoseCount: newDoseCount,
          };
          setFormData({
            ...formData,
            VaccineDetails: [...formData.VaccineDetails, vaccineData],
          });
          setNewVaccine({ id: "", unitreq: "" });
        }
      } else {
        console.warn(`VaccineID ${newVaccine.id} not found in vaccineOptions`);
      }
    }
  };

  const handleAlertConfirm = (action) => {
    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newVaccine.id
    );
    const uop = selectedVaccine.PriceDistribution || "350";
    const packageSize = selectedVaccine.PackageSize || 1;
    const newQuantity = parseInt(newVaccine.unitreq);
    const newDoseCount = Math.ceil(newQuantity * packageSize);

    if (action === "add") {
      const updatedDetails = formData.VaccineDetails.map((detail) => {
        if (detail.VaccineID === newVaccine.id) {
          const updatedQuantity = detail.RequestQuantity + newQuantity;
          const newTotalPrice = updatedQuantity * parseFloat(uop);
          const updatedDoseCount = Math.ceil(updatedQuantity * packageSize);
          return {
            ...detail,
            RequestQuantity: updatedQuantity,
            TotalPrice: newTotalPrice,
            DoseCount: updatedDoseCount,
          };
        }
        return detail;
      });
      setFormData({ ...formData, VaccineDetails: updatedDetails });
    } else if (action === "replace") {
      const updatedDetails = formData.VaccineDetails.map((detail) => {
        if (detail.VaccineID === newVaccine.id) {
          const newTotalPrice = newQuantity * parseFloat(uop);
          return {
            ...detail,
            RequestQuantity: newQuantity,
            TotalPrice: newTotalPrice,
            DoseCount: newDoseCount,
          };
        }
        return detail;
      });
      setFormData({ ...formData, VaccineDetails: updatedDetails });
    }
    setNewVaccine({ id: "", unitreq: "" });
    setIsAlertOpen(false);
  };

  const handleRemoveVaccine = (row) => {
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID
      ),
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineReservations",
        data: { ...formData, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData({ ...formData, ReservationID: response.ReservationID });
        setIsSubmitted(true);
        alert("สร้างใบจองสำเร็จ! เลขที่ใบจอง: " + response.ReservationID);
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      alert("เกิดข้อผิดพลาดในการสร้างใบจอง: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    
    const submitData = {
      ...formData,
      ReservationDate: formData.ReservationDate,
      documentDate: formData.documentDate,
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineReservationsByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกใบจองสำเร็จ!");
        //navigate("/vaccinereservationlist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการจองนี้?")) {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/cancelVaccineReservationsByID",
          data: { ReservationID: formData.ReservationID },
          sessionId: sessionId || "",
        });
        if (response.result === "success") {
          alert("ยกเลิกการจองสำเร็จ!");
          navigate("/vaccinereservationlist");
        } else {
          alert("เกิดข้อผิดพลาดในการยกเลิก: " + response.error);
        }
      } catch (error) {
        console.error("Error cancelling reservation:", error);
        alert("เกิดข้อผิดพลาดในการยกเลิก: " + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById("printable-reservation");
    const originalContent = document.body.innerHTML;
    document.body.innerHTML = printContent.outerHTML;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload(); // Restore the page after printing
  };

  const getWarehouseName = async (warehouseId) => {
    const warehouse = pickupPoints.find((point) => point.WarehouseID === warehouseId);
    if (warehouse) {
      return warehouse.WarehouseName;
    }
    // Fallback to API call if not found in pickupPoints
    return await fetchWarehouseById(warehouseId);
  };

  // printTable and exportToPDF functions remain unchanged
  const printTable = async () => {
    const warehouseName = await getWarehouseName(formData.PickupPoint);
    const periodEndDate = periodEnd ? periodEnd.toLocaleDateString("th-TH") : " ";
    const printContent = `
      <html>
        <head>
          <title>ใบสั่งจองวัคซีนออนไลน์</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h3 { text-align: center; margin: 10px 0; }
            .header-info { margin-bottom: 20px; }
            .header-row { display: flex; justify-content: margin-bottom: 5px; }
            .header-item { flex: 1; min-width: 250px; }
            .header-top { display:flex; justify-content: space-between; align-items:flex-start; }
            table { border-collapse: collapse; width: 100%; border: 1px solid black; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #d3d3d3; }
            .totals-row { font-weight: bold; background-color: #f0f0f0; }
            .totals-row td { text-align: right; }
            .totals-row td:nth-child(2) { text-align: left; }
            .notes { margin-top: 20px; }
            .notes p { margin: 5px 0; }
          </style>
        </head>
        <body>
          <div class="header-top">
            <div style="flex:1;">
              <h3>สำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์</h3>
              <h3>ใบสั่งจองวัคซีนออนไลน์</h3>
            </div>
          </div>
          <div class="header-info">
            <div class="header-row">
              <p class="header-item">เลขที่ใบจอง: ${formData.ReservationID}</p>
              <p class="header-item">วันที่เอกสาร: ${formData.documentDate.toLocaleDateString("th-TH")}</p>
            </div>
            <div class="header-row">
              <p class="header-item">ชื่อเกษตรกร: ${formData.FarmerName}</p>
            </div>
            <div class="header-row">
              <p class="header-item">เบอร์โทร: ${formData.FarmerTel}</p>
              <p class="header-item">อีเมล: ${formData.FarmerEmail}</p>
            </div>
            <div class="header-row">
              <p class="header-item">รอบการจัดส่ง: ${formData.DeliveryPeriod}</p>
              <p class="header-item">สถานะ: ${formData.ReservationStatus}</p>
            </div>
            <div class="header-row">
              <p class="header-item">จุดรับวัคซีน: ${warehouseName}</p>
            </div>
          </div>
          <table border="1px solid black" border-collapse="collapse" cellpadding="5" cellspacing="0">
            <thead>
              <tr>
                <th>รหัสวัคซีน</th>
                <th>ชื่อวัคซีน</th>
                <th>จำนวน (ขวด)</th>
                <th>จำนวน (โดส)</th>
                <th>ราคาต่อหน่วย (บาท)</th>
                <th>จำนวนเงินรวม (บาท)</th>
              </tr>
            </thead>
            <tbody>
              ${formData.VaccineDetails.map(
                (detail) => `
                  <tr>
                    <td>${detail.VaccineID}</td>
                    <td>${detail.name}</td>
                    <td style="text-align: right;">${Number(detail.RequestQuantity).toLocaleString("th-TH")}</td>
                    <td style="text-align: right;">${Number(detail.DoseCount).toLocaleString("th-TH")}</td>
                    <td style="text-align: right;">${Number(detail.UnitPrice).toLocaleString("th-TH", {
                      minimumFractionDigits: 2,
                    })}</td>
                    <td style="text-align: right;">${Number(detail.TotalPrice).toLocaleString("th-TH", {
                      minimumFractionDigits: 2,
                    })}</td>
                  </tr>
                `
              ).join("")}
              <tr class="totals-row">
                <td></td>
                <td>จำนวนเงินรวมทั้งสิ้น (บาท)</td>
                <td style="text-align: right;">${Number(totalQuantity).toLocaleString("th-TH")}</td>
                <td></td>
                <td></td>
                <td style="text-align: right;">${Number(totalPrice).toLocaleString("th-TH", {
                  minimumFractionDigits: 2,
                })}</td>
              </tr>
            </tbody>
          </table>          
          </div>

          <div class="notes">
            <p>หมายเหตุ : เอกสารนี้เป็นเอกสารการสั่งจองวัคซีนออนไลน์ ที่จัดทำขึ้นโดยระบบอัตโนมัติ ของสำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์</p>
            <p>1. เอกสารนี้ยังไม่ใช่การยืนยันการรับจองวัคซีน เจ้าหน้าที่จะตรวจสอบ และยืนยันการจองอีกครั้ง</p>
            <p>2. รายการจองวัคซีนนี้จะถูกจัดส่งตามรอบการจัดส่งที่เลือกไว้</p>
            <p>3. กรุณาตรวจสอบข้อมูลการจองวัคซีนให้ถูกต้อง โดยท่านสามารถแก้ไขข้อมูลได้ภายในวันที่กำหนด (${periodEndDate})</p>
            <p>4. หากท่านต้องการแก้ไขรายการจอง หรือยกเลิกการจองวัคซีนหลังจากวันที่ ${periodEndDate} กรุณาติดต่อเจ้าหน้าที่สำนักงานปศุสัตว์จังหวัดที่ท่านระบุเป็นจุดรับวัคซีน</p>
            <p>5. หากท่านต้องการสอบถามข้อมูลเพิ่มเติม กรุณาติดต่อเจ้าหน้าที่สำนักงานปศุสัตว์จังหวัด หรือสำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์</p>
          </div>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=600,height=800");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

const exportToPDF = async () => {
    const doc = new jsPDF();
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun-Regular.ttf", fontBase64);
      doc.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
      doc.setFont("helvetica");
    }
    const warehouseName = await getWarehouseName(formData.PickupPoint);
    const periodEndDate = periodEnd ? periodEnd.toLocaleDateString("th-TH") : " ";
    // Header
    doc.setFontSize(14);
    doc.text("สำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์", 105, 20, { align: "center" });
    doc.text("ใบสั่งจองวัคซีนออนไลน์", 105, 30, { align: "center" });
    doc.setFontSize(12);
    doc.text(`เลขที่ใบจอง: ${formData.ReservationID}`, 20, 40);
    doc.text(`วันที่เอกสาร: ${formData.documentDate.toLocaleDateString("th-TH")}`, 120, 40);
    doc.text(`ชื่อเกษตรกร: ${formData.FarmerName}`, 20, 50);
    //doc.text(`เลขประจำตัวผู้เสียภาษี: ${formData.RefTaxID || '-'}`, 120, 50);
    doc.text(`เบอร์โทร: ${formData.FarmerTel}`, 20, 60);
    doc.text(`อีเมล: ${formData.FarmerEmail}`, 120, 60);
    doc.text(`รอบการจัดส่ง: ${formData.DeliveryPeriod}`, 20, 70);
    doc.text(`สถานะ: ${formData.ReservationStatus}`, 120, 70);
    doc.text(`จุดรับวัคซีน: ${warehouseName}`, 20, 80);

    // Prepare table body with total row
    const totalQuantity = formData.VaccineDetails.reduce((sum, detail) => sum + detail.RequestQuantity, 0);
    const totalPrice = formData.VaccineDetails.reduce((sum, detail) => sum + detail.TotalPrice, 0);
    autoTable(doc, {
      startY: 90,
      head: [["รหัสวัคซีน", "ชื่อวัคซีน", "จำนวน (ขวด)", "จำนวน (โดส)", "ราคาต่อหน่วย (บาท)", "จำนวนเงินรวม (บาท)"]],
      body: [
        ...formData.VaccineDetails.map((detail) => [
        detail.VaccineID,
        detail.name,
        detail.RequestQuantity.toLocaleString(),
        detail.DoseCount.toLocaleString(),
        detail.UnitPrice.toLocaleString(),
        detail.TotalPrice.toLocaleString(),
      ]),
        // Totals row
        [
          "", // รหัสวัคซีน
          "จำนวนเงินรวมทั้งสิ้น (บาท)", // ชื่อวัคซีน
          Number(totalQuantity).toLocaleString("th-TH"), // จำนวน (ขวด)
          "", // จำนวนโดส
          "", // ราคาต่อหน่วย
          Number(totalPrice).toLocaleString("th-TH", {
            minimumFractionDigits: 2,
          }), // มูลค่ารวม
        ],
      ],
      theme: "grid",
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 9,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        fillColor: [255, 255, 255],
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [211, 211, 211],
        textColor: "black",
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 20 },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
      },
      didParseCell: (data) => {
        // Apply bold and light gray background to totals row
        if (data.row.section === "body" && data.row.cells[1]?.content === "จำนวนเงินรวมทั้งสิ้น (บาท)") {
          data.row.cells.forEach((cell) => {
            cell.styles.fontStyle = "bold";
            cell.styles.fillColor = [240, 240, 240]; // Light gray background
          });
        }
      },
    });

    // Add footer text (adjust based on your needs)
    const finalY = doc.lastAutoTable.finalY || 34;
    doc.setFontSize(8);
    doc.text(
      [
        "หมายเหตุ : เอกสารนี้เป็นเอกสารการสั่งจองวัคซีนออนไลน์ ที่จัดทำขึ้นโดยระบบอัตโนมัติ ของสำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์",
        "1. เอกสารนี้ยังไม่ใช่การยืนยันการรับจองวัคซีน เจ้าหน้าที่จะตรวจสอบ และยืนยันการจองอีกครั้ง",
        "2. รายการจองวัคซีนนี้จะถูกจัดส่งตามรอบการจัดส่งที่เลือกไว้",
        `3. กรุณาตรวจสอบข้อมูลการจองวัคซีนให้ถูกต้อง โดยท่านสามารถแก้ไขข้อมูลได้ภายในวันที่กำหนด (${periodEndDate})`,
        `4. หากท่านต้องการแก้ไขรายการจอง หรือยกเลิกการจองวัคซีนหลังจากวันที่ ${periodEndDate} กรุณาติดต่อเจ้าหน้าที่สำนักงานปศุสัตว์จังหวัดที่ท่านระบุเป็นจุดรับวัคซีน`,
        "5. หากท่านต้องการสอบถามข้อมูลเพิ่มเติม กรุณาติดต่อเจ้าหน้าที่สำนักงานปศุสัตว์จังหวัด หรือสำนักเทคโนโลยีชีวภัณฑ์สัตว์ กรมปศุสัตว์",
      ],
      14,
      finalY + 5
    );

    doc.save("VaccineReservationForm.pdf");
  };

  //const isEditable = formData.ReservationStatus === "ใบจองใหม่";
  //const isSaveButtonVisible = periodEnd ? new Date() <= periodEnd : true;
  const today = new Date();
  today.setHours(0, 0, 0, 0); // ตัดเวลาออก เหลือแค่วันที่

  const end = new Date(periodEnd);
  end.setHours(23, 59, 59, 999); // ให้สิ้นสุดจนถึงสิ้นวัน

  const isEditable =
  formData.ReservationStatus === "ใบจองใหม่" &&
  (periodEnd ? today <= end : true);


  const totalQuantity = formData.VaccineDetails.reduce((sum, detail) => sum + detail.RequestQuantity, 0);
  const totalPrice = formData.VaccineDetails.reduce((sum, detail) => sum + detail.TotalPrice, 0);

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faListOl} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringeIcon} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) =>
        isEditable ? (
          <Input
            type="number"
            value={row.RequestQuantity}
            onChange={(e) => handleQuantityChange(row.VaccineID, e.target.value)}
            min={0}
            size="sm"
            // MODIFICATION: Responsive width for the input
            width={{ base: "full", md: "100px" }}
            ml={{ base: 2, md: 10 }}
          />
        ) : (
          // MODIFICATION: Responsive margin for the box
          <Box width="100%" ml={{ base: 2, md: 10 }}>
            {row.RequestQuantity.toLocaleString()}
          </Box>
        ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวนโดส</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      sortable: true,
      cell: (row) => (
        // MODIFICATION: Responsive margin for the box
        <Box width="100%" ml={{ base: 2, md: 10 }}>
          {row.DoseCount.toLocaleString()}
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>ราคาต่อหน่วย (บาท)</span>
        </HStack>
      ),
      selector: (row) => row.UnitPrice,
      sortable: true,
      cell: (row) => (
        // MODIFICATION: Responsive margin for the box
        <Box width="100%" ml={{ base: 2, md: 10 }}>
          {row.UnitPrice.toLocaleString()}
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faDollarSign} />
          <span>มูลค่ารวม (บาท)</span>
        </HStack>
      ),
      selector: (row) => row.TotalPrice,
      sortable: true,
      cell: (row) => (
        // MODIFICATION: Responsive margin for the box
        <Box width="100%" ml={{ base: 2, md: 10 }}>
          {row.TotalPrice.toLocaleString()}
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>การดำเนินการ</span>
        </HStack>
      ),
      cell: (row) => (
        <IconButton.Delete
          icon={faTrash}
          onClick={() => handleRemoveVaccine(row)}
          label="ลบรายการ"
          width="100px"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  if (!sessionId) {
    return null;
  }

  return (
    <Box p={{ base: 4, md: 6 }} m={{ base: 2, md: 4 }} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <div id="printable-reservation">
        <StandardPageTitle title="รายการสั่งจองวัคซีน" />

        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="ข้อมูลเอกสารการสั่งจองวัคซีน"
            bgColor="iBlue.600"
            icon={faFileLines}
          />
          {/* MODIFICATION: Changed Grid to be responsive. It's 1 column on base and 4 on large screens. */}
          <Grid templateColumns={{ base: "1fr", lg: "repeat(4, 1fr)" }} gap={6} p={5}>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  เลขที่ใบสั่งจองวัคซีน
                </FormLabel>
                <Input
                  name="ReservationID"
                  value={formData.ReservationID}
                  isReadOnly
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.DeliveryPeriod}>
                <FormLabel fontSize="xl" color="gray">
                  รอบการจัดส่งวัคซีน
                </FormLabel>
                <Input
                  type="text"
                  name="DeliveryPeriod"
                  value={formData.DeliveryPeriod}
                  onChange={handleInputChange}
                placeholder="รอบการจัดส่ง"
                isReadOnly // ✅ ถ้าไม่ต้องการให้ผู้ใช้พิมพ์เอง
              />
                <FormErrorMessage>{errors.DeliveryPeriod}</FormErrorMessage>
              </FormControl>
            </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                วันที่เอกสาร
              </FormLabel>
              <Input
                type="text"
                value={new Date(formData.ReservationDate).toLocaleDateString("th-TH")}
                isReadOnly
              />
            </FormControl>
          </GridItem>
{/*                      
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  วันที่เอกสาร
                </FormLabel>
                <CustomDatePicker
                name="ReservationDate"
                value={formData.ReservationDate}
                  onChange={handleInputChange}
                  maxDate={new Date()} // วันนี้
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
*/}
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.PickupPoint}>
                <FormLabel fontSize="xl" color="gray">
                  จุดจำหน่ายที่ต้องการรับวัคซีน
                </FormLabel>
                <Select
                  name="PickupPoint"
                  value={formData.PickupPoint}
                  onChange={handleInputChange}
                  placeholder="เลือกจุดจำหน่าย"
                  colorScheme="iBlue"
                >
                  {pickupPoints.map((point) => (
                    <option key={point.WarehouseID} value={point.WarehouseID}>
                      {point.WarehouseName}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.PickupPoint}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.FarmerName}>
                <FormLabel fontSize="xl" color="gray">
                  ชื่อเกษตรกร
                </FormLabel>
                <Input
                  name="FarmerName"
                  value={formData.FarmerName}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.FarmerName}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  เลขประจำตัวผู้เสียภาษี
                </FormLabel>
                <Input
                  name="RefTaxID"
                  value={formData.RefTaxID}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  maxLength={30}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.FarmerTel}>
                <FormLabel fontSize="xl" color="gray">
                  เบอร์โทรติดต่อ
                </FormLabel>
                <Input
                  name="FarmerTel"
                  value={formData.FarmerTel}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.FarmerTel}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.FarmerEmail}>
                <FormLabel fontSize="xl" color="gray">
                  E-mail Address
                </FormLabel>
                <Input
                  name="FarmerEmail"
                  value={formData.FarmerEmail}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.FarmerEmail}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 4 }}>
              <FormControl mb={4}>
                <FormLabel fontSize="xl" color="gray">
                  วัตถุประสงค์การใช้งาน / รายละเอียดเพิ่มเติม
                </FormLabel>
                <Textarea
                  name="UsageObjective"
                  value={formData.UsageObjective}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 4 }}>
              <FormControl>
                <FormLabel fontSize="xl" color="gray">
                  สถานะรายการ
                </FormLabel>
                <Input
                  name="ReservationStatus"
                  value={formData.ReservationStatus}
                  onChange={handleInputChange}
                  isReadOnly
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
          </Grid>
          {!isSubmitted && (
            <Box p={5}>
              {/* MODIFICATION: Replaced HStack with a responsive Flex container */}
              <Flex
                direction={{ base: "column", md: "row" }}
                justifyContent={{ md: "flex-end" }}
                alignItems={{ base: "stretch", md: "center" }}
                gap={4}
                w="100%"
              >
                <IconButton.Cancel
                  onClick={() => navigate("/vaccinereservationlist")}
                />
                <IconButton.Submit
                  label="สร้างใบจองวัคซีน"
                  onClick={handleSubmit}
                />
              </Flex>
            </Box>
          )}
        </Box>

        {isSubmitted && (
          <>
            {isEditable && (
              <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
                <Box
                  mb={5}
                  borderRadius="full"
                  w="100%"
                  bgColor="iPurple.500"
                  color="white"
                  p={2}
                >
                  <HStack spacing={5} ml={5}>
                    <FontAwesomeIcon icon={faSyringe} color="white" size="xl" />
                    <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                      เลือกวัคซีนที่ต้องการสั่งจอง
                    </Heading>
                  </HStack>
                </Box>
                {/* MODIFICATION: Changed Grid to be responsive. It's 1 column on base and 3 on medium screens. */}
                <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={6} p={5}>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      เลือกวัคซีน
                    </FormLabel>
                    <Select
                      name="id"
                      value={newVaccine.id}
                      onChange={handleVaccineInputChange}
                      placeholder="เลือกวัคซีน"
                      colorScheme="iPurple"
                    >
                      {vaccineOptions.map((vaccine) => (
                        <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                          {vaccine.VaccineNameTH}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xl" color="gray">
                      จำนวน (ขวด)
                    </FormLabel>
                    <Input
                      name="unitreq"
                      type="number"
                      value={newVaccine.unitreq}
                      onChange={handleVaccineInputChange}
                      min={1}
                      colorScheme="iPurple"
                    />
                  </FormControl>
                  <Box mt={{ base: 4, md: 10 }}>
                    <IconButton.Add onClick={handleAddVaccine} h={9} w={{ base: "full", md: "auto" }}/>
                  </Box>
                </Grid>
              </Box>
            )}

            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
                <Box
                  mb={5}
                  borderRadius="full"
                  w="100%"
                  bgColor="iOrange.500"
                  color="white"
                  p={2}
                >
                  <HStack spacing={5} ml={5}>
                    <FontAwesomeIcon icon={faTable} color="white" size="xl" />
                    <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                      รายการวัคซีนที่ต้องการสั่งจอง
                    </Heading>
                  </HStack>
                </Box>
                <Box p={{ base: 0, md: 5 }} overflowX="auto">
                  <DataTable
                    columns={columns}
                    data={formData.VaccineDetails}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                    customStyles={tableCustomStyles}
                    subHeader
                    subHeaderComponent={
                      <Box width="100%" textAlign="right" p={2}>
                        <strong>รวมจำนวน (ขวด): </strong>{totalQuantity.toLocaleString()} | 
                        <strong> รวมมูลค่ารวม (บาท): </strong>{totalPrice.toLocaleString()}
                      </Box>
                    }
                  />
                </Box>
              </Box>

              <Box p={5}>
                {/* MODIFICATION: Replaced HStack with a responsive Flex container for action buttons */}
                 <Flex
                  direction={{ base: "column", md: "row" }}
                  justifyContent={{ md: "flex-end" }}
                  alignItems={{ base: "stretch", md: "center" }}
                  gap={4}
                  wrap="wrap"
                  w="100%"
                >
                  <IconButton.Back
                    onClick={() => navigate("/vaccinereservationlist")}
                    label="ย้อนกลับ"
                  />
                  
                  {isEditable && (
                    <IconButton.Cancel
                      onClick={handleCancelReservation}
                      label="ยกเลิกการจอง"
                    />
                  )}
                  {isEditable && (
                    <IconButton.Save
                      onClick={handleConfirm}
                      isLoading={isLoading}
                    />
                  )}
                  <IconButton.Print onClick={printTable} label="พิมพ์ใบจอง"/>
                  <IconButton.ExportPdf onClick={exportToPDF} />
                </Flex>
              </Box>

              <AlertDialog
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => handleAlertConfirm("cancel")}
              >
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      วัคซีนซ้ำ
                    </AlertDialogHeader>
                    <AlertDialogBody>
                      มีวัคซีนนี้อยู่ในรายการสั่งจองแล้ว กรุณาเลือกการดำเนินการ:
                    </AlertDialogBody>
                    <AlertDialogFooter>
                      <Button
                        ref={cancelRef}
                        onClick={() => handleAlertConfirm("cancel")}
                      >
                        ยกเลิก
                      </Button>
                      <Button
                        colorScheme="blue"
                        onClick={() => handleAlertConfirm("add")}
                        ml={3}
                      >
                        เพิ่มจำนวน (ขวด)
                      </Button>
                      <Button
                        colorScheme="green"
                        onClick={() => handleAlertConfirm("replace")}
                        ml={3}
                      >
                        ปรับจำนวน (ขวด)
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>
            </>
          )}
        </div>
      </Box>
    );
  };

  export default VaccineReservationForm;